import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Star, Loader2, Image as ImageIcon, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AdminLayout from '@/components/admin/AdminLayout';
import { testimonialsAPI, uploadImage } from '@/lib/api';
import { toast } from 'sonner';

const initialForm = {
  name: '', text: '', rating: 5, avatar: '', badge: 'Compra Verificada', is_active: true,
};

export const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const fetchTestimonials = async () => {
    try {
      const res = await testimonialsAPI.getAll();
      setTestimonials(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTestimonials(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(initialForm);
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, text: t.text, rating: t.rating, avatar: t.avatar || '', badge: t.badge || 'Compra Verificada', is_active: t.is_active !== false });
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadImage(file);
      setForm(prev => ({ ...prev, avatar: res.data.url }));
      toast.success('Imagem enviada');
    } catch (err) {
      toast.error('Erro ao enviar imagem');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.text.trim()) {
      toast.error('Nome e texto sao obrigatorios');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await testimonialsAPI.update(editingId, form);
        toast.success('Depoimento atualizado');
      } else {
        await testimonialsAPI.create(form);
        toast.success('Depoimento criado');
      }
      setDialogOpen(false);
      fetchTestimonials();
    } catch (e) { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await testimonialsAPI.delete(deleteId);
      toast.success('Depoimento removido');
      setDeleteId(null);
      fetchTestimonials();
    } catch (e) { toast.error('Erro ao remover'); }
  };

  return (
    <AdminLayout title="Depoimentos">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">{testimonials.length} depoimentos cadastrados</p>
          <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600" data-testid="add-testimonial-btn">
            <Plus className="w-4 h-4 mr-2" /> Novo Depoimento
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : testimonials.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-slate-400">Nenhum depoimento cadastrado</CardContent></Card>
        ) : (
          <div className="grid gap-3">
            {testimonials.map((t) => (
              <Card key={t.id} className={`${!t.is_active ? 'opacity-50' : ''}`} data-testid={`testimonial-${t.id}`}>
                <CardContent className="p-4 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-200 flex-shrink-0">
                    {t.avatar ? (
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold">
                        {t.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-900">{t.name}</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{t.badge}</span>
                      {!t.is_active && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Inativo</span>}
                    </div>
                    <div className="flex gap-0.5 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < t.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}`} />
                      ))}
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{t.text}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(t)} className="text-slate-400 hover:text-blue-500">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteId(t.id)} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Depoimento' : 'Novo Depoimento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-100 flex-shrink-0 relative">
                {form.avatar ? (
                  <img src={form.avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <Label>Foto do Autor</Label>
                <div className="flex gap-2">
                  <Input
                    value={form.avatar}
                    onChange={(e) => setForm(prev => ({ ...prev, avatar: e.target.value }))}
                    placeholder="URL da imagem ou envie..."
                    className="text-xs"
                  />
                  <label className="cursor-pointer">
                    <Button variant="outline" size="sm" asChild>
                      <span><ImageIcon className="w-4 h-4" /></span>
                    </Button>
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  </label>
                </div>
              </div>
            </div>
            <div>
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Maria Silva" />
            </div>
            <div>
              <Label>Depoimento</Label>
              <Textarea value={form.text} onChange={(e) => setForm(p => ({ ...p, text: e.target.value }))} placeholder="Texto do depoimento..." rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Avaliacao (1-5)</Label>
                <div className="flex gap-1 mt-1">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} onClick={() => setForm(p => ({ ...p, rating: n }))} className="focus:outline-none">
                      <Star className={`w-6 h-6 ${n <= form.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Badge</Label>
                <Input value={form.badge} onChange={(e) => setForm(p => ({ ...p, badge: e.target.value }))} placeholder="Compra Verificada" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Ativo</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm(p => ({ ...p, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingId ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Alert */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Depoimento?</AlertDialogTitle>
            <AlertDialogDescription>Esta acao nao pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminTestimonials;
